import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useAppDispatch } from 'src/AppContext';
import './index.scoped.scss';
import { RouteComponentProps } from 'react-router-dom';
import { useUser } from 'src/UserContext';
import useCustomHistory from 'src/hooks/useCustomHistory';
import { getProduct } from 'src/services/checkout';
import { getCurrency, isKorean } from 'src/services/util';
import PromotionChart from 'src/components/PromotionChart';
import { Trans, useTranslation } from 'react-i18next';
import { userProducts, userProductsCreated } from 'src/services/api';
import { useModal } from 'src/ModalContext';
import { PRODUCT_CATEGORY, PRODUCT_GUIDE, PRODUCT_REGION } from 'src/constants/product';
import i18n from 'src/i18n';
import { Shape1, Shape2, Shape3, Shape4 } from './Shape';
import StickyButton from 'src/components/StickyButton';

interface MatchParams {
  index: string;
}

// 원가
const getPrice1 = (detailObj: CheckoutProductData, isUnit: boolean) => {
  if (detailObj.promotionAmount && detailObj.promotionAmount > 0 && detailObj.promotionCycle > 0) {
    const unit = isUnit ? getCurrency(detailObj.region) : '';
    return unit + detailObj.amount.toLocaleString();
  }
  return '';
};
// 프로모션 가격 또는 프로모션이 없는 경우 원가
const getPrice2 = (detailObj: CheckoutProductData, isUnit: boolean) => {
  const unit = isUnit ? getCurrency(detailObj.region) : '';
  if (detailObj.promotionAmount && detailObj.promotionAmount > 0 && detailObj.promotionCycle > 0) {
    return unit + detailObj.promotionAmount.toLocaleString();
  } else {
    return unit + detailObj.amount.toLocaleString();
  }
};
const getDurationTranslation = (durationType: string) => {
  if (durationType === 'HOUR') return 'product/purchase/billing_period_hour';
  else if (durationType === 'DAY') return 'product/purchase/billing_period_day';
  else if (durationType === 'MONTH') return 'product/purchase/billing_period_month';
  return '';
};

const SHAPE_COMPONENT = {
  'shape-1': <Shape1 />,
  'shape-2': <Shape2 />,
  'shape-3': <Shape3 />,
  'shape-4': <Shape4 />,
};

export default function PurchaseDetail(props: RouteComponentProps<MatchParams>): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useCustomHistory();
  const { isInit, isLogin } = useUser();
  const [detailObj, setDetailObj] = useState<CheckoutProductData>();
  const [isUsed, setIsUsed] = useState<boolean>(false);
  const { showModal, errorModal } = useModal();

  const index = props.match.params.index;

  useLayoutEffect(() => {
    dispatch({ type: 'SET_GNB_LEFT', name: 'back' });
    dispatch({ type: 'SET_GNB_RIGHT', name: 'hidden' });
    return () => {
      dispatch({ type: 'RESET_GNB_LEFT' });
      dispatch({ type: 'RESET_GNB_RIGHT' });
    };
  }, []);

  useEffect(() => {
    if (!isInit) return;

    getProduct(index)
      .then((res) => {
        if (res) {
          // @TODO for not launching bixby
          if (res.category === PRODUCT_CATEGORY.BIXBY) {
            showModal({
              message: t('common/bixby_not_launch_message'),
              success: () => {
                history.goBack();
              },
            });
          }
          if (isLogin) {
            userProducts({
              category: res.category,
            }).then((res) => {
              setIsUsed(!!res.products.find(({ productId }) => +index === productId));
            });
          }
          setDetailObj(res);
        } else {
          showModal({
            message: t('payment/not_found_product'),
            success: () => {
              history.push('/');
            },
          });
        }
      })
      .catch(errorModal);
  }, [isInit, isLogin]);

  const goPayment = useCallback(
    async (e, obj) => {
      e.stopPropagation();
      e.preventDefault();
      if (isLogin) {
        const product = await getProduct(obj.id);
        userProductsCreated()
          .then((res) => {
            if (res.products.length > 0) {
              const activatedProduct = res.products.find((value) => value.category === product.category);

              if (activatedProduct) {
                showModal({
                  message: t('product/purchase/already_ticket_exists', {
                    product: isKorean() ? res.products[0].koName : res.products[0].enName,
                  }),
                  success: () => {
                    return;
                  },
                });
                return;
              }
            }
            history.push(`/payment`, {
              productId: obj.id,
              afterPaymentEndpoint: location.pathname,
            });
          })
          .catch(errorModal);
      } else {
        showModal({
          message: t('drawer/login_error'),
          success: () => {
            history.push('/login');
          },
          fail: () => {
            return;
          },
        });
      }
    },
    [isLogin, history],
  );
  return (
    <>
      {detailObj && (
        <>
          <div className="container mt-6">
            <div className="flex">
              {/* TODO 임시 */}
              {/* <div className="N_10_R text-white bg-topaz rounded-sm px-2 h-5 flex justify-center items-center mr-1">HOT</div> */}
              {detailObj.subscription && (
                <div className="N_10_R text-white bg-light-grey-blue rounded-sm px-2 h-5 flex justify-center items-center">
                  {t('payment/complete/badge_billing')}
                </div>
              )}
            </div>
            <div className="N_20_R text-black mt-3">{isKorean() ? detailObj.koName : detailObj.enName}</div>
            <div className="N_14_R text-brown-grey line-through mt-7">
              {detailObj.subscription && isUsed ? '' : getPrice1(detailObj, true)}
            </div>
            <div className="flex justify-between">
              <div className="N_22_M text-black">
                {detailObj.subscription && isUsed ? getPrice1(detailObj, true) : getPrice2(detailObj, true)}
              </div>
              <div className="N_12_R text-greyish-brown flex justify-center items-center">
                {detailObj.subscription && (
                  <Trans i18nKey={getDurationTranslation(detailObj.durationType)} values={{ period: detailObj.duration }}></Trans>
                )}
              </div>
            </div>
            {!isUsed && detailObj.promotionCycle > 0 && detailObj.promotionAmount > 0 && (
              <>
                <hr className="border-ice-blue mt-6 -mx-6"></hr>
                <div className="">
                  <PromotionChart product={detailObj} />
                  <div className="disclaimer relative N_12_R text-greyish-brown mt-8 ml-3">
                    <Trans
                      i18nKey={
                        detailObj.region === PRODUCT_REGION.GLOBAL
                          ? 'product/purchase/disclaimer1_foreigner'
                          : 'product/purchase/disclaimer1_korean'
                      }
                      values={{ cycle: detailObj.promotionCycle, amount: getPrice2(detailObj, false) }}
                    ></Trans>
                    {/* <div className="">{detailObj.promotionCycle}회차 까지는 할인된 금액인 {getPrice2(detailObj)} 결제이며.</div> */}
                    <div className="">
                      <Trans
                        i18nKey={
                          detailObj.region === PRODUCT_REGION.GLOBAL
                            ? 'product/purchase/disclaimer2_foreigner'
                            : 'product/purchase/disclaimer2_korean'
                        }
                        components={{ span: <span className="N_12_M" /> }}
                        values={{ cycle: detailObj.promotionCycle + 1, amount: getPrice1(detailObj, false) }}
                      ></Trans>
                      {/* <span className="N_12_M">{detailObj.promotionCycle + 1}회차 부터는 정상금액인 {getPrice1(detailObj)}으로 결제</span>됩니다. */}
                    </div>
                  </div>
                  <div className="disclaimer relative N_12_R text-greyish-brown mt-2 ml-3">
                    <div className="">
                      <Trans i18nKey="product/purchase/disclaimer3" components={{ span: <span className="N_12_M" /> }}></Trans>
                      {/* 동일상품 정기결제 해지 후 <span className="N_12_M">재결제시에는 할인 프로모션이 적용되지 않습니다.</span> (1회차 부터 정상가 결제) */}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="relative w-full mt-10">
            {PRODUCT_GUIDE[detailObj.category + i18n.languages[0]].map(([className, imageName, shape], index) => (
              <div key={index} className={'relative w-full ' + className}>
                {shape && SHAPE_COMPONENT[shape]}
                <img className="relative max-w-360 w-full mx-auto" src={`/assets/images/product/${imageName}.png`} />
              </div>
            ))}
          </div>
          <div className="container">
            {/* <div
              className="relative mt-10 -mx-6 pt-7 pl-6 pb-12"
              style={{
                backgroundColor: '#9ba2b1',
              }}
            >
              <div
                className="absolute left-0 top-0 w-full h-full bg-right bg-no-repeat bg-contain"
                style={{
                  backgroundImage:
                    detailObj.category === PRODUCT_CATEGORY.KARAOKE
                      ? `url(/assets/images/banners/detail/common-img-img-banner-karaoke-detail.svg)`
                      : `url(/assets/images/banners/detail/common-img-img-banner-bixby-detail.svg)`, // TODO en으로 변경
                }}
              />
              <div className="relative">
                <Icon name="tv-w" size="40" />
                <div className="N_16_M text-white mt-3" dangerouslySetInnerHTML={{ __html: t('product/purchase/banner_1') }} />
                <div className="N_12_R text-white mt-2" dangerouslySetInnerHTML={{ __html: t('product/purchase/banner_2') }} />
              </div>
            </div> */}
            <ul className=" pt-6 pb-10 list-disc">
              <li className="N_12_R text-brown-grey">{t('product/purchase/disclaimer6')}</li>
              <li className="N_12_R text-brown-grey">{t('product/purchase/disclaimer7')}</li>
              <li className="N_12_R text-brown-grey">{t('product/purchase/disclaimer4')}</li>
              <li className="N_12_R text-brown-grey">{t('product/purchase/disclaimer5')}</li>
            </ul>
          </div>
          <div
            className="sticky bottom-0 w-full bg-bluey-purple"
            // style={{
            //   bottom: 'env(safe-area-inset-bottom, 10px)',
            // }}
          >
            <StickyButton
              title={t('product/purchase/button_purchase')}
              onClick={(e) => {
                goPayment(e, detailObj);
              }}
            />
          </div>
        </>
      )}
    </>
  );
}
